// frontend/src/hooks/chat/useChatQueries.ts
import { useCallback, useEffect } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  getChannelByEpisodeChatChannelsByEpisodeEpisodeIdGet,
  getChatChannel,
  getChatDirectory,
  getChatInbox,
  getChatMessages,
  listRoles,
  markChatChannelRead,
  sendChatMessage,
  startChatConversation,
} from '../../services/api';
import type {
  ChannelHeader,
  DirectoryRow,
  InboxRow,
  MessageOut,
} from '../../services/api';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { FeatureFlag } from '../../constants/featureFlags';
import { useStore } from '../../store';

export const MESSAGE_PAGE_SIZE = 50;
const INBOX_STALE_TIME_MS = 30_000;

export const chatKeys = {
  all: ['chat'] as const,
  inbox: () => ['chat', 'inbox'] as const,
  channel: (id: string) => ['chat', 'channel', id] as const,
  messages: (id: string) => ['chat', 'messages', id] as const,
  directory: (q: string) => ['chat', 'directory', q] as const,
  // Sorted: the same member set is one conversation whatever order it was picked in.
  conversation: (userIds: string[]) =>
    ['chat', 'conversation', [...userIds].sort().join(',')] as const,
  announcerRole: () => ['chat', 'announcer-role'] as const,
  announcers: (groupId: string) => ['chat', 'announcers', groupId] as const,
  send: () => ['chat', 'send'] as const,
  careTeam: (episodeId: string) => ['chat', 'care-team', episodeId] as const,
};

export function useInbox() {
  return useQuery({
    queryKey: chatKeys.inbox(),
    queryFn: async () => {
      const { data } = await getChatInbox({ throwOnError: true });
      return (data ?? []) as InboxRow[];
    },
    staleTime: INBOX_STALE_TIME_MS,
  });
}

/** Source of truth for the badge and the tab title: the inbox rows. */
export function useUnreadTotal(): number {
  const { data } = useInbox();
  return (data ?? []).reduce((sum, r) => sum + (r.unread_count ?? 0), 0);
}

export function useChannel(channelId: string | null) {
  return useQuery({
    queryKey: chatKeys.channel(channelId ?? ''),
    queryFn: async () => {
      const { data } = await getChatChannel({
        path: { channel_id: channelId! },
        throwOnError: true,
      });
      return data as ChannelHeader;
    },
    enabled: !!channelId,
  });
}

export function useCareTeamChannel(episodeId: string | undefined) {
  const hasChat = useStore(state =>
    state.hasFeatureFlag(FeatureFlag.CHAT, 'workos')
  );
  return useQuery({
    queryKey: chatKeys.careTeam(episodeId ?? ''),
    queryFn: async () => {
      try {
        const response =
          await getChannelByEpisodeChatChannelsByEpisodeEpisodeIdGet({
            path: { episode_id: episodeId as string },
            throwOnError: true,
          });
        return response.data;
      } catch (error) {
        // A 404 or a 403 both mean "no button" and stay silent, the way a
        // stale session's flag-off query stays silent — returned as a
        // successful "no channel" rather than a query error, so the shared
        // query cache's error logging (services/queryClient.ts) never fires
        // for the expected case. Anything else is logged (AGENTS.md: never
        // show a raw server error, but do log it) and left to fail the query.
        const status = getHttpStatus(error);
        if (status === 404 || status === 403) return null;
        console.error('Failed to load the care-team channel', error);
        throw error;
      }
    },
    enabled: hasChat && Boolean(episodeId),
    retry: false,
    staleTime: INBOX_STALE_TIME_MS,
  });
}

export function useMessages(channelId: string | null) {
  const queryClient = useQueryClient();
  return useInfiniteQuery({
    queryKey: chatKeys.messages(channelId ?? ''),
    initialPageParam: undefined as number | undefined,
    queryFn: async ({ pageParam }) => {
      const { data } = await getChatMessages({
        path: { channel_id: channelId! },
        query: {
          limit: MESSAGE_PAGE_SIZE,
          ...(pageParam ? { before: pageParam } : {}),
        },
        throwOnError: true,
      });
      const rows = (data ?? []) as MessageOut[];
      confirmFetched(queryClient, channelId!, rows);
      return rows;
    },
    // `before=` pages return newest first, so the oldest seq of the last page
    // is the cursor for the page before it.
    getNextPageParam: last =>
      last.length < MESSAGE_PAGE_SIZE
        ? undefined
        : Math.min(...last.map(m => m.seq)),
    enabled: !!channelId,
  });
}

/** Oldest first, one row per seq. Frames and pages can overlap; seq de-dupes. */
export function flattenMessages(
  pages: MessageOut[][] | undefined
): MessageOut[] {
  const bySeq = new Map<number, MessageOut>();
  for (const page of pages ?? []) for (const m of page) bySeq.set(m.seq, m);
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

export type MessagePages =
  { pages: MessageOut[][]; pageParams: unknown[] } | undefined;

/**
 * Merge arrivals into the newest page (`pages[0]`), never as a new trailing page.
 * `useInfiniteQuery` reads `hasNextPage` and the next cursor from the LAST page,
 * so a trailing one-row arrival page would end paging of older history for the
 * rest of the session, and would leave `pageParams` shorter than `pages`.
 */
export function mergeIntoNewestPage(
  old: MessagePages,
  arrivals: MessageOut[]
): MessagePages {
  if (!old) return { pages: [arrivals], pageParams: [undefined] };
  const cached = new Set(old.pages.flat().map(m => m.seq));
  const fresh = arrivals.filter(m => !cached.has(m.seq));
  if (fresh.length === 0) return old;
  const [newest = [], ...older] = old.pages;
  return { ...old, pages: [[...newest, ...fresh], ...older] };
}

/**
 * Merge arrivals into a channel's messages cache, and merge them again when a
 * fetch that was already running settles. An infinite query builds its result
 * from the cache snapshot taken when the fetch started (an older page, or a
 * refetch), so without the second merge that result would drop an arrival that
 * landed while it ran.
 */
export function mergeArrivals(
  queryClient: QueryClient,
  channelId: string,
  arrivals: MessageOut[]
): void {
  const queryKey = chatKeys.messages(channelId);
  const apply = () =>
    queryClient.setQueryData(queryKey, (old: MessagePages) =>
      mergeIntoNewestPage(old, arrivals)
    );
  apply();
  const cache = queryClient.getQueryCache();
  const query = cache.find({ queryKey, exact: true });
  if (!query || query.state.fetchStatus === 'idle') return;
  const unsubscribe = cache.subscribe(event => {
    if (event.query !== query) return;
    if (event.type === 'removed') return unsubscribe();
    if (query.state.fetchStatus !== 'idle') return;
    unsubscribe();
    apply();
  });
}

/**
 * Seqs that are in the cache only because a send response put them there. A
 * send response is one row, not a list, so a message another person sent just
 * before it may not be cached yet: these seqs must not move the fetch or read
 * cursor. A list fetch that returns a seq removes it from the set.
 */
const sendOnlySeqs = new WeakMap<QueryClient, Map<string, Set<number>>>();

function sendOnlySet(queryClient: QueryClient, channelId: string) {
  const byChannel =
    sendOnlySeqs.get(queryClient) ?? new Map<string, Set<number>>();
  sendOnlySeqs.set(queryClient, byChannel);
  const seqs = byChannel.get(channelId) ?? new Set<number>();
  byChannel.set(channelId, seqs);
  return seqs;
}

/**
 * Messages queries that a list fetch has completed for. Keyed on the query
 * itself, so an evicted cache starts again as "not fetched".
 */
const fetchedHistories = new WeakSet<object>();

function messagesQuery(queryClient: QueryClient, channelId: string) {
  return queryClient
    .getQueryCache()
    .find({ queryKey: chatKeys.messages(channelId), exact: true });
}

function confirmFetched(
  queryClient: QueryClient,
  channelId: string,
  rows: MessageOut[]
): void {
  const seqs = sendOnlySet(queryClient, channelId);
  for (const row of rows) seqs.delete(row.seq);
  const query = messagesQuery(queryClient, channelId);
  if (query) fetchedHistories.add(query);
}

/**
 * Whether a list fetch has completed for the channel's cached history. Until
 * one has, the cache holds at most send responses, and `after=0` would drain
 * the channel from its oldest row.
 */
export function hasFetchedHistory(
  queryClient: QueryClient,
  channelId: string
): boolean {
  const query = messagesQuery(queryClient, channelId);
  return !!query && fetchedHistories.has(query);
}

/**
 * The newest seq that a list fetch has returned for the channel: the `after=`
 * cursor for a live fetch and the seq to mark read. `rows` are oldest first.
 * Returns 0 when no fetched row is cached.
 */
export function lastFetchedSeq(
  queryClient: QueryClient,
  channelId: string,
  rows: MessageOut[]
): number {
  const seqs = sendOnlySet(queryClient, channelId);
  for (let i = rows.length - 1; i >= 0; i--) {
    if (!seqs.has(rows[i].seq)) return rows[i].seq;
  }
  return 0;
}

export function useDirectory(query: string) {
  return useQuery({
    queryKey: chatKeys.directory(query),
    queryFn: async () => {
      const { data } = await getChatDirectory({
        query: { q: query, limit: 50 },
        throwOnError: true,
      });
      return (data ?? []) as DirectoryRow[];
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * The single GROUP-scope role (EMR-1876). Fetched once per session. The caller
 * needs the failure too: without the role id no announcer can be added.
 */
export function useAnnouncerRoleId(enabled: boolean): {
  roleId: string | undefined;
  isError: boolean;
} {
  const { data, isError } = useQuery({
    queryKey: chatKeys.announcerRole(),
    queryFn: async () => {
      const { data } = await listRoles({
        query: { scope: 'group' },
        throwOnError: true,
      });
      return data ?? [];
    },
    enabled,
    staleTime: Infinity,
  });
  return { roleId: data?.[0]?.id, isError };
}

/**
 * Both throttles are keyed on the QueryClient, not held in a module variable:
 * a module variable survives between tests in one file, so the second test's
 * refetch would be pushed onto a timer and its mark-read dropped.
 */
const inboxRefetchState = new WeakMap<
  QueryClient,
  { lastAt: number; timer?: ReturnType<typeof setTimeout> }
>();

/**
 * Refetch the inbox at most once a second. A burst of frames (an announcement
 * fan-out) must cost one request, not one per frame.
 */
export function refetchInboxCoalesced(queryClient: QueryClient): void {
  const s = inboxRefetchState.get(queryClient) ?? { lastAt: 0 };
  inboxRefetchState.set(queryClient, s);
  const since = Date.now() - s.lastAt;
  if (since >= 1000) {
    s.lastAt = Date.now();
    void queryClient.refetchQueries({ queryKey: chatKeys.inbox() });
    return;
  }
  if (s.timer) return;
  s.timer = setTimeout(() => {
    s.timer = undefined;
    s.lastAt = Date.now();
    void queryClient.refetchQueries({ queryKey: chatKeys.inbox() });
  }, 1000 - since);
}

/**
 * Fetch one page after `afterSeq`, merge it into the channel's cache, and
 * return how many rows it held. A full page means more may follow.
 */
export async function appendMessagesAfter(
  queryClient: QueryClient,
  channelId: string,
  afterSeq: number
): Promise<number> {
  const { data } = await getChatMessages({
    path: { channel_id: channelId },
    query: { after: afterSeq, limit: MESSAGE_PAGE_SIZE },
    throwOnError: true,
  });
  const arrivals = (data ?? []) as MessageOut[];
  if (arrivals.length === 0) return 0;
  confirmFetched(queryClient, channelId, arrivals);
  mergeArrivals(queryClient, channelId, arrivals);
  return arrivals.length;
}

type SendVariables = {
  channelId: string;
  body: string;
  client_id: string;
  /** EMR-1878: kept on the mutation, so a Retry resends the same files. */
  attachment_ids: string[];
};

/** A send the server has not confirmed yet, or one that failed. */
export interface PendingSend {
  client_id: string;
  body: string;
  status: 'pending' | 'failed';
  attachment_ids: string[];
}

/**
 * The one send path, used by the Channel view and the New message view. The
 * mutation cache is global to the QueryClient, so a pending row survives the
 * New message → Channel switch, and the mutation-level `onSuccess` still runs
 * after the view that started the send unmounts.
 */
export function useSendMessage() {
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationKey: chatKeys.send(),
    mutationFn: async ({
      channelId,
      body,
      client_id,
      attachment_ids,
    }: SendVariables) => {
      const { data } = await sendChatMessage({
        path: { channel_id: channelId },
        body: { body, client_id, attachment_ids },
        throwOnError: true,
      });
      return data as MessageOut;
    },
    onSuccess: (message, { channelId }) => {
      const cached = queryClient.getQueryData(
        chatKeys.messages(channelId)
      ) as MessagePages;
      if (!cached?.pages.some(page => page.some(m => m.seq === message.seq))) {
        sendOnlySet(queryClient, channelId).add(message.seq);
      }
      mergeArrivals(queryClient, channelId, [message]);
      refetchInboxCoalesced(queryClient);
    },
    onError: error => console.error('Chat send failed', error),
  });
  const send = useCallback(
    // One id per attempt, reused on retry: the server's unique index on
    // (channel, sender, client_id) makes the retry return the first row.
    (
      channelId: string,
      body: string,
      clientId?: string,
      attachmentIds: string[] = []
    ) => {
      const client_id = clientId ?? crypto.randomUUID();
      return mutateAsync({
        channelId,
        body,
        client_id,
        attachment_ids: attachmentIds,
      }).catch((error: unknown) => {
        // 403 (announcers only) and 409 (archived) are permanent: a Retry can
        // only fail again, so the failed row is dropped. Network and 5xx
        // failures keep it, with Retry.
        // 422 (a file the server will not link) is permanent too: the ids a
        // Retry would resend are the ones it refused.
        const status = getHttpStatus(error);
        if (status === 403 || status === 409 || status === 422) {
          // Keep the text as the channel's draft unless a new one was typed
          // meanwhile. Here, not in a view, so a send started from New
          // message (which has unmounted) keeps it too.
          const store = useStore.getState();
          if (!store.chatDrafts[channelId]) {
            store.setChatDraft(channelId, body);
          }
          // `can_write` and `archived` live on the header, so the header is
          // what must refetch to show the locked or archived notice. `all`:
          // from New message the Channel view may not have subscribed yet, and
          // its first header load, still in flight, would answer can_write.
          void queryClient.invalidateQueries({
            queryKey: chatKeys.channel(channelId),
            refetchType: 'all',
          });
          const cache = queryClient.getMutationCache();
          cache
            .findAll({
              mutationKey: chatKeys.send(),
              predicate: m =>
                (m.state.variables as SendVariables | undefined)?.client_id ===
                client_id,
            })
            .forEach(m => cache.remove(m));
        }
        throw error;
      });
    },
    [mutateAsync, queryClient]
  );
  return { send };
}

/**
 * Pending and failed sends for one channel, one row per client_id. The mutation
 * cache lists attempts oldest first, so a retry's state replaces the failed
 * attempt's, and a success removes the row (the server row is in the messages cache).
 */
export function usePendingSends(channelId: string): PendingSend[] {
  const attempts = useMutationState({
    filters: {
      mutationKey: chatKeys.send(),
      predicate: m =>
        (m.state.variables as SendVariables | undefined)?.channelId ===
        channelId,
    },
    select: m => ({
      status: m.state.status,
      variables: m.state.variables as SendVariables,
    }),
  });
  const byClientId = new Map<string, PendingSend>();
  for (const { status, variables } of attempts) {
    if (status === 'success') byClientId.delete(variables.client_id);
    else if (status === 'pending' || status === 'error')
      byClientId.set(variables.client_id, {
        client_id: variables.client_id,
        body: variables.body,
        status: status === 'error' ? 'failed' : 'pending',
        attachment_ids: variables.attachment_ids,
      });
  }
  return [...byClientId.values()];
}

type MarkReadThrottle = {
  lastAt: number;
  seq: number;
  timer?: ReturnType<typeof setTimeout>;
};
const markReadState = new WeakMap<QueryClient, Map<string, MarkReadThrottle>>();
const activeMarkReadUsers = new WeakMap<QueryClient, number>();

/**
 * The panel must still show this channel when the POST goes out, not only when
 * the call was made.
 *
 * Checked here rather than passed in by the caller: the throttle record is
 * shared by every caller for a channel, so a guard held on it is cleared by the
 * next call, and the two callers (the channel view's own effect and the live
 * catch-up) do overlap inside one window.
 */
function panelShowsChannel(channelId: string): boolean {
  const { isChatOpen, chatView, activeChannelId } = useStore.getState();
  return isChatOpen && chatView === 'channel' && activeChannelId === channelId;
}

/**
 * Forward-only read cursor, at most one write a second per channel. A call inside
 * the window is not dropped: the newest seq is sent when the window ends, so an
 * open channel's unread stays 0 when a frame lands right after it opened.
 * A hidden tab does not mark read: the post is skipped and the seq is kept, so
 * the next call made while the tab is visible sends it. A panel that no longer
 * shows the channel is the same: the person is not reading it any more.
 */
export function useMarkRead() {
  const queryClient = useQueryClient();
  useEffect(() => {
    activeMarkReadUsers.set(
      queryClient,
      (activeMarkReadUsers.get(queryClient) ?? 0) + 1
    );
    return () => {
      const remaining = (activeMarkReadUsers.get(queryClient) ?? 1) - 1;
      if (remaining > 0) {
        activeMarkReadUsers.set(queryClient, remaining);
      } else {
        activeMarkReadUsers.delete(queryClient);
        const byChannel = markReadState.get(queryClient);
        if (byChannel) {
          for (const state of byChannel.values()) {
            if (state.timer) {
              clearTimeout(state.timer);
              state.timer = undefined;
            }
          }
        }
      }
    };
  }, [queryClient]);
  return useCallback(
    (channelId: string, lastReadSeq: number) => {
      const byChannel =
        markReadState.get(queryClient) ?? new Map<string, MarkReadThrottle>();
      markReadState.set(queryClient, byChannel);
      const s = byChannel.get(channelId) ?? { lastAt: 0, seq: 0 };
      byChannel.set(channelId, s);
      s.seq = Math.max(s.seq, lastReadSeq);

      const post = () => {
        s.timer = undefined;
        if ((activeMarkReadUsers.get(queryClient) ?? 0) === 0) return;
        if (document.visibilityState !== 'visible') return;
        if (!panelShowsChannel(channelId)) return;
        s.lastAt = Date.now();
        const seq = s.seq;
        void markChatChannelRead({
          path: { channel_id: channelId },
          body: { last_read_seq: seq },
          throwOnError: true,
        })
          .then(() => {
            queryClient.setQueryData(
              chatKeys.inbox(),
              (rows: InboxRow[] | undefined) =>
                (rows ?? []).map(r =>
                  r.channel_id === channelId
                    ? { ...r, unread_count: 0, last_read_seq: seq }
                    : r
                )
            );
            // The patch is cache-only and has no last_message. Refetch after
            // the POST committed, so the server's unread count is already 0.
            refetchInboxCoalesced(queryClient);
          })
          .catch(error => console.error('Chat mark-read failed', error));
      };

      const since = Date.now() - s.lastAt;
      if (since >= 1000) {
        post();
        return;
      }
      if (!s.timer) s.timer = setTimeout(post, 1000 - since);
    },
    [queryClient]
  );
}

export function useStartConversation() {
  const find = useCallback(async (userIds: string[]) => {
    try {
      const { data } = await startChatConversation({
        body: { user_ids: userIds, create: false },
        throwOnError: true,
      });
      return data as ChannelHeader;
    } catch (error) {
      // 404 is "this set has no conversation yet", which the view shows as its
      // empty state — not the errors-table 404.
      if (getHttpStatus(error) === 404) return null;
      throw error;
    }
  }, []);

  const create = useCallback(async (userIds: string[]) => {
    const { data } = await startChatConversation({
      body: { user_ids: userIds },
      throwOnError: true,
    });
    return data as ChannelHeader;
  }, []);

  return { find, create };
}
